import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Sun, Moon } from 'lucide-react';
import { Avatar } from '@/components/ui';
import { WidgetRenderer } from '@/components/profile';
import { useTheme } from '@/hooks/useTheme';
import { getPageByUsername } from '@/services/pages';
import type { Page } from '@/types';

interface PublicProfileProps {
  username: string;
}

const PublicProfile = ({ username }: PublicProfileProps) => {
  const { t } = useTranslation();
  const { isDark, toggleTheme } = useTheme();
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const pageData = await getPageByUsername(username.toLowerCase());
        if (pageData) {
          setPage(pageData);
        }
      } catch (err) {
        console.error('Failed to load profile:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [username]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-dark-900">
        <Loader2 className="w-8 h-8 animate-spin text-lime-500" />
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-dark-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-dark-800 dark:text-white mb-2">
            {t('profile.profileNotFound')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {t('profile.profileNotFoundMessage')}
          </p>
          <a
            href="/"
            className="inline-flex items-center justify-center px-6 py-3 bg-lime-400 text-dark-800 font-medium rounded-full hover:bg-lime-500 transition-colors"
          >
            {t('profile.goToKlipr')}
          </a>
        </div>
      </div>
    );
  }

  const sortedWidgets = [...(page.widgets || [])].sort((a, b) => a.order - b.order);

  return (
    <div className="min-h-screen py-8 px-4 bg-gray-100 dark:bg-dark-900 relative">
      {/* Theme Toggle */}
      <button
        onClick={toggleTheme}
        className="absolute top-4 right-4 p-2.5 rounded-full bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-dark-700 transition-colors shadow-sm"
        aria-label="Toggle theme"
      >
        {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      <div className="max-w-lg mx-auto">
        {/* Profile Header */}
        <div className="text-center mb-8">
          <Avatar
            src={page.avatarUrl || undefined}
            name={page.title}
            size="xl"
            className="mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-dark-800 dark:text-white mb-2">
            {page.title}
          </h1>
          {page.bio && (
            <p className="text-base text-dark-800/70 dark:text-gray-400">
              {page.bio}
            </p>
          )}
        </div>

        {/* Widgets */}
        <div className="space-y-4">
          {sortedWidgets.map((widget) => (
            <WidgetRenderer key={widget.id} widget={widget} userId={page.userId} />
          ))}

          {sortedWidgets.length === 0 && (
            <div className="text-center py-12 opacity-50">
              <p className="text-dark-800 dark:text-gray-400">
                {t('profile.noContentYet')}
              </p>
            </div>
          )}
        </div>

        {/* Powered by */}
        <div className="text-center mt-12">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-sm text-dark-800/50 dark:text-gray-500 hover:opacity-70 transition-opacity"
          >
            <div className="w-5 h-5 rounded-md flex items-center justify-center text-xs font-bold bg-lime-400">
              K
            </div>
            {t('common.poweredByKlipr')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default PublicProfile;
